import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    TextField,
    Slider,
    Tooltip,
    Chip,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    MenuItem,
    Select,
    InputLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useHelpContent } from '../../utils/HelpUtils';
import { Category } from '../../types/Category';
import { fetchCategoryWithChildren } from '../../services/CategoryService';

export type ExpandImportMode = 'category_tree' | 'content_only';
export type ExpandContentTargetMode = 'existing' | 'new_subcategory';

export interface ExpandContentTarget {
    mode: ExpandContentTargetMode;
    targetCategoryId?: string;
    newSubcategoryName?: string;
}

interface ContentCategoryOption {
    id: string;
    label: string;
}

interface ExpandTreeDialogProps {
    open: boolean;
    onClose: () => void;
    onProceed: (
        context: string,
        maxDepth: number,
        treeStrategy: 'top_down' | 'bottom_up',
        importMode: ExpandImportMode,
        contentTarget?: ExpandContentTarget,
        charsPerSegment?: number,
    ) => Promise<void>;
    fileNames: string[];
    currentDocumentName: string;
    documentCategoryId?: string;
}

const DEFAULT_CHARS_PER_SEGMENT = 600;
const MIN_CHARS_PER_SEGMENT = 100;
const MAX_CHARS_PER_SEGMENT = 20000;

function isContentCategoryNode(category: Category): boolean {
    return category.contentCategory === true || (category as { isContentCategory?: boolean }).isContentCategory === true;
}

function collectContentCategoryOptions(
    category: Category,
    path: string[] = [],
    isRoot = false,
): ContentCategoryOption[] {
    const currentPath = isRoot
        ? path
        : [...path, category.name?.trim() || 'Category'];
    const options: ContentCategoryOption[] = [];
    if (isContentCategoryNode(category)) {
        const labelPath = isRoot
            ? [category.name?.trim() || 'Category']
            : currentPath;
        options.push({
            id: category.id,
            label: labelPath.join(' / '),
        });
    }
    for (const child of category.children ?? []) {
        options.push(...collectContentCategoryOptions(child, currentPath, false));
    }
    return options;
}

const ExpandTreeDialog: React.FC<ExpandTreeDialogProps> = ({
    open,
    onClose,
    onProceed,
    fileNames,
    currentDocumentName,
    documentCategoryId,
}) => {
    const [context, setContext] = useState('');
    const [maxDepth, setMaxDepth] = useState(1);
    const [charsPerSegment, setCharsPerSegment] = useState<number>(DEFAULT_CHARS_PER_SEGMENT);
    const [treeStrategy, setTreeStrategy] = useState<'top_down' | 'bottom_up'>('top_down');
    const [importMode, setImportMode] = useState<ExpandImportMode>('category_tree');
    const [contentTargetMode, setContentTargetMode] = useState<ExpandContentTargetMode>('existing');
    const [targetCategoryId, setTargetCategoryId] = useState('');
    const [newSubcategoryName, setNewSubcategoryName] = useState('');
    const [rootCategory, setRootCategory] = useState<Category | null>(null);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loading, setLoading] = useState(false);
    const { getTooltipTextForState } = useHelpContent();

    useEffect(() => {
        if (!open || !documentCategoryId) {
            setRootCategory(null);
            return;
        }
        let cancelled = false;
        const loadRoot = async () => {
            setLoadingCategories(true);
            try {
                const tree = await fetchCategoryWithChildren(documentCategoryId);
                if (!cancelled) {
                    setRootCategory(tree);
                }
            } catch (error) {
                console.error('Failed to load document categories for expand dialog', error);
                if (!cancelled) {
                    setRootCategory(null);
                }
            } finally {
                if (!cancelled) {
                    setLoadingCategories(false);
                }
            }
        };
        void loadRoot();
        return () => {
            cancelled = true;
        };
    }, [open, documentCategoryId]);

    const contentCategoryOptions = useMemo(
        () => (rootCategory ? collectContentCategoryOptions(rootCategory, [], true) : []),
        [rootCategory],
    );

    const rootHasSubcategories = Boolean(
        rootCategory &&
            !isContentCategoryNode(rootCategory) &&
            (rootCategory.children?.length ?? 0) > 0,
    );

    useEffect(() => {
        if (!open) return;
        if (contentCategoryOptions.length === 1) {
            setTargetCategoryId(contentCategoryOptions[0].id);
            setContentTargetMode('existing');
        } else if (
            targetCategoryId &&
            !contentCategoryOptions.some((opt) => opt.id === targetCategoryId)
        ) {
            setTargetCategoryId('');
        }
    }, [open, contentCategoryOptions, targetCategoryId]);

    useEffect(() => {
        if (importMode === 'content_only' && !rootHasSubcategories) {
            setContentTargetMode('existing');
        }
    }, [importMode, rootHasSubcategories]);

    const handleProceed = async () => {
        setLoading(true);
        try {
            const contentTarget: ExpandContentTarget | undefined =
                importMode === 'content_only'
                    ? contentTargetMode === 'new_subcategory'
                        ? {
                              mode: 'new_subcategory',
                              newSubcategoryName: newSubcategoryName.trim(),
                          }
                        : {
                              mode: 'existing',
                              targetCategoryId,
                          }
                    : undefined;
            const clampedChars = Math.max(
                MIN_CHARS_PER_SEGMENT,
                Math.min(MAX_CHARS_PER_SEGMENT, charsPerSegment),
            );
            await onProceed(context.trim(), maxDepth, treeStrategy, importMode, contentTarget, clampedChars);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setContext('');
        setMaxDepth(1);
        setCharsPerSegment(DEFAULT_CHARS_PER_SEGMENT);
        setTreeStrategy('top_down');
        setImportMode('category_tree');
        setContentTargetMode('existing');
        setTargetCategoryId('');
        setNewSubcategoryName('');
        setRootCategory(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isProceedDisabled = () => {
        if (loading || loadingCategories) {
            return true;
        }
        if (importMode !== 'content_only') {
            return false;
        }
        if (contentTargetMode === 'new_subcategory') {
            return !newSubcategoryName.trim();
        }
        return !targetCategoryId;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Add to document from files</DialogTitle>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    {(loading || loadingCategories) && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Current Document: <strong>{currentDocumentName}</strong>
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            New Files to Process:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {fileNames.map((fileName, index) => (
                                <Chip key={index} label={fileName} variant="outlined" size="small" />
                            ))}
                        </Box>
                    </Box>

                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">Import mode</FormLabel>
                        <RadioGroup
                            value={importMode}
                            onChange={(e) => setImportMode(e.target.value as ExpandImportMode)}
                        >
                            <FormControlLabel
                                value="category_tree"
                                control={<Radio />}
                                label="Integrate with category structure (match existing folders, add new ones)"
                            />
                            <FormControlLabel
                                value="content_only"
                                control={<Radio />}
                                label="Content only (group segments, no new category folders)"
                            />
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {importMode === 'content_only'
                                ? 'Similar segments are clustered and merged into content rows. Choose where to place them in the document.'
                                : 'The AI places matching segments in existing categories and suggests new folders for the rest.'}
                        </Typography>
                    </FormControl>
                </Box>

                {importMode === 'content_only' && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Place content in
                        </Typography>
                        <RadioGroup
                            value={contentTargetMode}
                            onChange={(e) =>
                                setContentTargetMode(e.target.value as ExpandContentTargetMode)
                            }
                        >
                            <FormControlLabel
                                value="existing"
                                control={<Radio />}
                                label="Existing content category"
                            />
                            {rootHasSubcategories && (
                                <FormControlLabel
                                    value="new_subcategory"
                                    control={<Radio />}
                                    label="New subcategory under document root"
                                />
                            )}
                        </RadioGroup>
                        {contentTargetMode === 'existing' ? (
                            contentCategoryOptions.length > 0 ? (
                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                    <InputLabel id="expand-content-target-label">Content category</InputLabel>
                                    <Select
                                        labelId="expand-content-target-label"
                                        label="Content category"
                                        value={targetCategoryId}
                                        onChange={(e) => setTargetCategoryId(e.target.value)}
                                    >
                                        {contentCategoryOptions.map((opt) => (
                                            <MenuItem key={opt.id} value={opt.id}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    No content categories found in this document. Add a content category first,
                                    or use a document whose root already has subcategories.
                                </Alert>
                            )
                        ) : (
                            <TextField
                                fullWidth
                                size="small"
                                label="New subcategory name"
                                value={newSubcategoryName}
                                onChange={(e) => setNewSubcategoryName(e.target.value)}
                                placeholder="e.g. Imported notes"
                                sx={{ mt: 1 }}
                                helperText="A new content category will be created under the document root."
                            />
                        )}
                    </Box>
                )}
                
                <Typography variant="body1" gutterBottom>
                    <strong>Optional Context:</strong>{' '}
                    {importMode === 'content_only'
                        ? 'Optional hints for segmentation (domain, interview type, etc.).'
                        : 'Provide additional context to help the AI better integrate new content with your existing category structure.'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Examples of helpful context:
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "This is a continuation of the previous content"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "These files contain related but different topics"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "Organize by chronological order"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "Focus on technical implementation details"
                    </Typography>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Context (Optional)"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Describe how this new content relates to your existing structure..."
                    sx={{ mb: 3 }}
                />

                {importMode === 'category_tree' && (
                <>
                <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">
                            <Tooltip title={getTooltipTextForState('expand-tree-strategy', true)} placement="top">
                                <span>Building strategy</span>
                            </Tooltip>
                        </FormLabel>
                        <RadioGroup
                            row
                            value={treeStrategy}
                            onChange={(e) => setTreeStrategy(e.target.value as 'top_down' | 'bottom_up')}
                        >
                            <FormControlLabel
                                value="top_down"
                                control={<Radio />}
                                label="Top-down (fewer broad categories first)"
                            />
                            <FormControlLabel
                                value="bottom_up"
                                control={<Radio />}
                                label="Bottom-up (finer categories, then group upward)"
                            />
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Applies to new categories for content that does not match existing folders. Top-down merges less;
                            bottom-up starts with more leaf buckets.
                        </Typography>
                    </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Maximum Tree Depth: {maxDepth}
                    </Typography>
                    
                    <Tooltip title="Set the maximum number of levels deep new categories can be created">
                        <Slider
                            value={maxDepth}
                            onChange={(_, value) => setMaxDepth(value as number)}
                            min={1}
                            max={5}
                            marks
                            valueLabelDisplay="auto"
                            sx={{ mt: 1 }}
                        />
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                        Controls how deep new category hierarchies can be created
                    </Typography>
                </Box>
                </>
                )}

                <Box sx={{ mb: 3 }}>
                    <TextField
                        type="number"
                        label="Characters per section"
                        value={charsPerSegment}
                        onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            setCharsPerSegment(Number.isNaN(parsed) ? DEFAULT_CHARS_PER_SEGMENT : parsed);
                        }}
                        onBlur={() =>
                            setCharsPerSegment((prev) =>
                                Math.max(MIN_CHARS_PER_SEGMENT, Math.min(MAX_CHARS_PER_SEGMENT, prev)),
                            )
                        }
                        inputProps={{ min: MIN_CHARS_PER_SEGMENT, max: MAX_CHARS_PER_SEGMENT, step: 100 }}
                        variant="outlined"
                        size="small"
                        helperText={`Approximate size of each content section. Consecutive segments are merged up to this many characters before a new section starts (default ${DEFAULT_CHARS_PER_SEGMENT}).`}
                        sx={{ width: 260 }}
                    />
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    {importMode === 'content_only' ? (
                        <>
                            <Typography variant="body2">
                                Segments will be grouped and merged into content rows with no new folder structure.
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                • Review the preview, then apply to add rows to your chosen category
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2">
                                The AI will analyze your existing category structure and:
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                • Place new content segments in existing categories when there&apos;s a good match
                            </Typography>
                            <Typography variant="body2" component="div">
                                • Suggest new categories for content that doesn&apos;t fit existing structure
                            </Typography>
                            <Typography variant="body2" component="div">
                                • Maintain your current organization while expanding it intelligently
                            </Typography>
                        </>
                    )}
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={isProceedDisabled()}
                    startIcon={<AddIcon />}
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpandTreeDialog;
